import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

/**
 * SendQRModal — sends the QR scan link to a tenant via email and/or SMS.
 *
 * Email/SMS providers (Resend / Twilio) are MOCKED on the backend until real
 * keys are added. The modal still calls the real API and a record is persisted
 * so the user gets full UX feedback now and zero rework later.
 */
export default function SendQRModal({ open, onClose, vehicle, defaultEmail, defaultPhone }) {
  const [channels, setChannels] = useState({ email: true, sms: false });
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const list = Object.entries(channels)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (list.length === 0) {
      toast.error("Pick at least one channel");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/qr/send", {
        vehicle_id: vehicle.id,
        channels: list,
        to_email: email,
        to_phone: phone,
      });
      const mock = data.sent.some((s) => s.status === "sent_mock");
      toast.success(
        mock
          ? "Queued (Resend/Twilio MOCKED — connect keys to deliver)"
          : "Sent successfully"
      );
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="send-qr-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Send QR to tenant</DialogTitle>
          <DialogDescription>
            Pick channels. Email uses Resend; SMS uses Twilio.
            Both are <b>MOCKED</b> until API keys are connected — records are still persisted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <ChannelToggle
              icon={Mail}
              label="Email"
              active={channels.email}
              onClick={() => setChannels((c) => ({ ...c, email: !c.email }))}
              tid="send-toggle-email"
            />
            <ChannelToggle
              icon={MessageSquare}
              label="SMS"
              active={channels.sms}
              onClick={() => setChannels((c) => ({ ...c, sms: !c.sms }))}
              tid="send-toggle-sms"
            />
          </div>

          {channels.email && (
            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">Email address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                placeholder="tenant@example.com"
                data-testid="send-email"
              />
            </div>
          )}
          {channels.sms && (
            <div className="space-y-2">
              <Label className="mono-tiny text-[#71717A]">Phone number</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11"
                placeholder="+15551234567"
                data-testid="send-phone"
              />
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="h-11" data-testid="send-cancel">
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={busy}
            className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
            data-testid="send-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelToggle({ icon: Icon, label, active, onClick, tid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      className={`h-14 rounded-md border-2 flex items-center justify-center gap-2 transition-all ${
        active
          ? "border-[#0F8F4F] bg-[#0F8F4F]/5 text-[#0F8F4F]"
          : "border-[#E4E4E7] text-[#09090B]"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="font-medium">{label}</span>
    </button>
  );
}
